/**
 * Lightweight ZEC/USD price source.
 *
 * Pulled from CoinGecko's free `/simple/price` endpoint which doesn't
 * need a key and tolerates the low call volume a single Mini App user
 * generates. Cached in module-level state for 60s so we don't re-hit
 * on every component mount.
 *
 * Failure is silent — the wallet UI just falls back to ZEC-only labels
 * when we can't fetch a price. We never throw out of here.
 */

const CACHE_TTL_MS = 60_000;
const URL = "https://api.coingecko.com/api/v3/simple/price?ids=zcash&vs_currencies=usd";

let cached: { usd: number; fetchedAt: number } | null = null;
let inflight: Promise<number | null> | null = null;

export async function getZecUsdPrice(): Promise<number | null> {
  const now = Date.now();
  if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.usd;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await fetch(URL);
      if (!r.ok) return cached?.usd ?? null;
      const j = (await r.json()) as { zcash?: { usd?: number } };
      const usd = j?.zcash?.usd;
      if (typeof usd !== "number" || !Number.isFinite(usd) || usd <= 0) {
        return cached?.usd ?? null;
      }
      cached = { usd, fetchedAt: Date.now() };
      return usd;
    } catch {
      return cached?.usd ?? null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Format a USD value with reasonable rounding for a wallet display. */
export function formatUsd(usd: number): string {
  if (usd >= 1000) return `$${usd.toFixed(0)}`;
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(6)}`;
}

/** Convert ZEC zats to a formatted USD string. Returns null if no price. */
export function zatsToUsd(zats: number, zecPrice: number | null): string | null {
  if (zecPrice == null || zecPrice <= 0) return null;
  const zec = zats / 1e8;
  const usd = zec * zecPrice;
  return formatUsd(usd);
}
